import { useEffect, useRef } from 'react'
import Message from './Message'
import TypingIndicator from './TypingIndicator'
import './MessageList.css'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
}

interface MessageListProps {
  messages: Message[]
  isLoading?: boolean
}

export default function MessageList({ messages, isLoading = false }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messageListRef = useRef<HTMLDivElement>(null)
  const isInitialLoadRef = useRef(true)

  // Scroll to bottom when messages change or typing indicator appears
  useEffect(() => {
    if (messages.length === 0 && !isLoading) {
      isInitialLoadRef.current = true
      return
    }

    // For initial load, use auto scroll (instant)
    // For new messages, use smooth scroll
    const scrollBehavior = isInitialLoadRef.current ? 'auto' : 'smooth'
    
    // Use requestAnimationFrame + setTimeout to ensure DOM is fully rendered
    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: scrollBehavior })
        isInitialLoadRef.current = false
      }
    }, isInitialLoadRef.current ? 200 : 50)

    return () => clearTimeout(scrollTimer)
  }, [messages, isLoading])

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="message-list empty" ref={messageListRef}>
        <div className="empty-state">
          <p>Start a conversation by typing a message below.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="message-list" ref={messageListRef}>
      {messages.map((message) => (
        <Message key={message.id} message={message} />
      ))}
      {isLoading && <TypingIndicator />}
      <div ref={messagesEndRef} />
    </div>
  )
}

